/*
 * 回忆点滴第二层：那一天。
 *
 * 旧版对应 `packages/components/src/characterUserProjection/
 * CharacterRelationDiaryModule.tsx`。
 *
 * ⚠ **点开某一天看到的不是日记本身，是那天的对话列表。**
 * 日记退到顶上一颗「点击查看心情日记」的按钮后面，点了才弹出来。
 *
 * 这个顺序不是排版偏好。日记是她对那天的复述，对话是那天真正发生的事——
 * 把复述摆在第一位，用户翻回忆时先读到的是二手的概括，
 * 而他真正想找的「那天我们聊了什么」还要再点一层。旧版就是这么排的。
 *
 * ## 时间轴正序
 *
 * 外层日记列表倒序（先看最近的一天），进到某一天之后正序（按发生顺序重走一遍）。
 * 两层方向相反是对的：倒着列会让「早上她没回我，下午才聊起来」这种
 * 一天之内的因果反过来。排序在服务端做，这里不再排一次。
 */

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell, Sheet } from "../../components/ui";
import { IconCalendar, IconHeartOutline, IconHeartSolid, IconRetry } from "../../components/Icons";
import { fetchDay, rewriteRelationshipDiary, type DayView } from "../../services/me-api";
import { ApiError } from "../../services/api-client";
import { newActionId } from "../../services/action-id";
import { notifyRelationshipChanged } from "../../services/relationship-events";
import { useAppearance } from "../../state/session";
import { useResource } from "../../state/resource";
import { ChannelConversationSheet } from "../channel/ChannelConversationSheet";
import { toParagraphs, timeRangeLabel } from "./format";
import { RegenerationConfirm } from "./RegenerationConfirm";
import "../pages.css";

export function DayPage() {
  /*
   * 路由参数直接当日期用，不在客户端算「今天」——那需要知道业务时区，
   * 而时区是服务端的事（`shared/util/day.ts` 是它的单一实现处）。
   * 形状不对时服务端返回 400，页面走失败态，不在这里预判。
   */
  const { date = "" } = useParams<{ date: string }>();

  const data = useResource<DayView>((signal) => fetchDay(date, signal), [date]);
  const day = data.state.phase === "ready" ? data.state.data : null;
  const appearance = useAppearance();

  const [diaryOpen, setDiaryOpen] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const rewriteActionId = useRef<string | null>(null);
  const [selectedChannelThreadId, setSelectedChannelThreadId] = useState<string | null>(null);

  const diary = day?.diary ?? null;
  const conversations = day?.conversations ?? [];
  const channelEntries = (day?.channelPlatforms ?? [])
    .flatMap((platform) =>
      platform.threads.map((thread) => ({ platform, thread })),
    )
    .sort((left, right) => left.thread.startedAt.localeCompare(right.thread.startedAt));
  const selectedChannelEntry =
    channelEntries.find((entry) => entry.thread.id === selectedChannelThreadId) ?? null;
  const failed = data.state.phase === "failed";
  const recoveryPending = day?.recovery?.pending ?? 0;
  const diaryRewritePowerCost = day?.diaryRewritePowerCost ?? null;
  const recoveryPolls = useRef(0);

  const openRewrite = () => {
    if (diaryRewritePowerCost === null) return;
    rewriteActionId.current = newActionId();
    setRewriteError(null);
    setRewriteOpen(true);
  };

  const rewriteDiary = async () => {
    const actionId = rewriteActionId.current ?? newActionId();
    rewriteActionId.current = actionId;
    setRewriting(true);
    setRewriteError(null);
    try {
      await rewriteRelationshipDiary(date, actionId);
      notifyRelationshipChanged();
      setRewriteOpen(false);
      data.refresh();
    } catch (cause) {
      setRewriteError(cause instanceof Error ? cause.message : "这篇日记没能重写好");
      if (!(cause instanceof ApiError) || (cause.kind !== "offline" && cause.kind !== "timeout")) {
        rewriteActionId.current = newActionId();
      }
    } finally {
      setRewriting(false);
    }
  };

  useEffect(() => {
    if (!day || recoveryPending === 0) {
      recoveryPolls.current = 0;
      return;
    }
    if (recoveryPolls.current >= 80) return;
    const timer = window.setTimeout(() => {
      recoveryPolls.current += 1;
      data.refresh();
    }, 3_000);
    return () => window.clearTimeout(timer);
  }, [day, data.refresh, recoveryPending]);

  /* 标题就是列表里那一行：「日期-标题」。没有日记就只有日期。 */
  const heading = diary?.title ? `${date}-${diary.title}` : date;

  return (
    <PageShell
      icon={<IconCalendar size={20} />}
      title={heading}
      closeTo="/memories"
      help={
        <>
          <p>这是那一天的样子：你们聊过的每一段都在下面，按发生的先后排着。</p>
          <p>
            顶上那颗心点开，是{appearance.name}当天睡前写的心情日记——
            她眼里的那一天。
          </p>
          <p>
            其他平台的对话也会原样列在这里；夜里整理出的标题和记忆碎片，
            会和本地对话一样放在展开层顶部。
          </p>
        </>
      }
      actions={
        failed ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      {data.state.phase === "loading" ? <LoadingState label="正在翻看那一天" /> : null}

      {day && recoveryPending > 0 ? (
        <p className="inline-hint">那天还有回忆没整理好，正在补写，完成后会自动出现。</p>
      ) : null}

      {/* 必须写完整判断而不是用 failed：布尔量不参与类型收窄。 */}
      {data.state.phase === "failed" ? (
        <PageNotice
          scene="memory"
          error={data.state.error}
          onRetry={data.reload}
          expected={[
            "那一天的心情日记（标题、正文、标签）",
            "那一天的每一段对话，带时间范围与标题",
            "已绑定账号在其他平台留下的对话",
          ]}
        />
      ) : null}

      {/*
        日记入口在最顶上，居中，左右各一条横线——旧版是 antd-mobile 的
        `<Divider>` 里塞一颗圆角主按钮，这里用同一个形状。

        没有日记就整颗不画，只留一句说明。摆一颗点了弹空白的按钮更糟。
      */}
      {diary ? (
        <div className="diary-cta">
          <Button tone="primary" onClick={() => setDiaryOpen(true)}>
            <IconHeartOutline size={15} />
            点击查看心情日记
            <IconHeartOutline size={15} />
          </Button>
        </div>
      ) : null}

      {day && !diary && recoveryPending === 0 && channelEntries.length > 0 ? (
        <p className="inline-hint">
          这一天有站外对话，但心情日记还没写好。
        </p>
      ) : null}

      {day && !diary && recoveryPending === 0 && channelEntries.length === 0 ? (
        <p className="inline-hint">
          这一天的心情日记还没写好。{appearance.name}都是夜里才动笔的。
        </p>
      ) : null}

      {channelEntries.length > 0 || conversations.length > 0 ? (
        <div className="timeline">
          {channelEntries.map(({ platform, thread }) => (
            <button
              key={`${platform.providerKey}:${thread.id}`}
              type="button"
              className="timeline__item"
              onClick={() => setSelectedChannelThreadId(thread.id)}
            >
              <span className="timeline__entry">
                <span className="timeline__scope">【{thread.scopeLabel}】</span>
                {day?.channelMemory?.title || thread.preview || "这一天的站外对话"}
              </span>
            </button>
          ))}

          {conversations.map((item) => (
            /*
              路径上带的是 item.id —— 那是**记忆碎片的 id，不是会话 id**。
              一次长对话按上下文折叠点切成多段，每段各有标题与记忆碎片；
              用会话 id 的话，长对话的两条会点进去看到一模一样的内容。
            */
            <Link
              key={item.id}
              to={`/memories/${encodeURIComponent(date)}/${item.id}`}
              className="timeline__item"
            >
              <span className="timeline__entry">
                <span className="timeline__time">
                  {timeRangeLabel(item.startedAt, item.finishedAt)}
                </span>
                {item.title ?? "没留下标题的一段"}
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      {day && conversations.length === 0 && channelEntries.length === 0 && recoveryPending === 0 ? (
        <p className="inline-hint">
          这一天没有留下对话。也可能是刚聊完——她要过一会儿才把这段记下来。
        </p>
      ) : null}

      <Sheet
        open={diaryOpen}
        title={
          <>
            <IconHeartSolid size={16} />
            心情日记
            <IconHeartSolid size={16} />
          </>
        }
        label="心情日记"
        onClose={() => setDiaryOpen(false)}
      >
        {diary ? (
          <article className="diary">
            <h3 className="diary__title">{diary.title ?? "这一天"}</h3>

            {/*
              标签排在标题与正文中间，与记忆碎片同一个结构（关键词在上、
              一条横线、正文在下）。它们是这一天的索引，先看见几个词再读
              整段，比读完一整页再在末尾看到几个标签有用。
            */}
            {diary.tags.length > 0 ? (
              <div className="fragment__tags">
                {diary.tags.map((tag) => (
                  <span key={tag} className="chip chip--brand">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            {diary.excerpt ? (
              <div className="diary__body">
                {toParagraphs(diary.excerpt).map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="inline-hint">这一天她还没写下什么。</p>
            )}

            <div className="memory-regeneration-action">
              <Button
                tone="quiet"
                className="memory-regeneration-button"
                onClick={openRewrite}
              >
                让{appearance.name}重写
              </Button>
            </div>
          </article>
        ) : null}
      </Sheet>

      {diaryRewritePowerCost !== null ? (
        <RegenerationConfirm
          open={rewriteOpen}
          title={`让${appearance.name}重写这篇日记吗？`}
          description={`她会重新回想这一天，并写出一篇新的心情日记。这次会消耗 ${diaryRewritePowerCost} 点灵力。`}
          confirmLabel={`消耗 ${diaryRewritePowerCost} 灵力，让${appearance.name}重写`}
          pendingLabel="正在重写…"
          pending={rewriting}
          error={rewriteError}
          onConfirm={() => void rewriteDiary()}
          onClose={() => setRewriteOpen(false)}
        />
      ) : null}

      <ChannelConversationSheet
        open={selectedChannelEntry !== null}
        title={
          selectedChannelEntry
            ? `${date} · ${selectedChannelEntry.platform.platformLabel}`
            : `${date} · 站外相遇`
        }
        threads={selectedChannelEntry ? [selectedChannelEntry.thread] : []}
        hasEarlier={selectedChannelEntry?.platform.hasEarlier ?? false}
        memory={day?.channelMemory ?? null}
        speakerName={appearance.name}
        avatarUrl={appearance.avatarUrl}
        onClose={() => setSelectedChannelThreadId(null)}
      />
    </PageShell>
  );
}
